<script setup lang="ts">
import { ScalarButton } from '@scalar/components/button'
import { ScalarTooltip } from '@scalar/components/tooltip'
import type {
  ApiReferenceEvents,
  WorkspaceEventBus,
} from '@scalar/workspace-store/events'
import type { XScalarEnvironment } from '@scalar/workspace-store/schemas/extensions/document/x-scalar-environments'
import { computed } from 'vue'

import { CollapsibleSection } from '@/v2/components/layout'
import { useLocalization } from '@/v2/features/localization'

import RequestTable from './RequestTable.vue'
import type { TableRow, TableRowUpsertPayload } from './RequestTableRow.vue'

const {
  rows,
  exampleKey,
  environment,
  title,
  globalRoute,
  showAddRowPlaceholder = true,
  eventBus,
} = defineProps<{
  rows: TableRow[]
  exampleKey: string
  title: string
  label?: string
  /** Explain restrictions on parameter editing when needed. */
  description?: string
  invalidParams?: Set<string>
  globalRoute?: string
  showAddRowPlaceholder?: boolean
  environment: XScalarEnvironment
  eventBus: WorkspaceEventBus
}>()

const emit = defineEmits<{
  (
    e: 'upsert',
    index: number,
    payload: ApiReferenceEvents['operation:upsert:parameter']['payload'] & {
      shouldRenameExpandedRow?: boolean
    },
  ): void
  (e: 'delete', payload: { index: number }): void
  (e: 'deleteAll'): void
  /** Select a value for a grouped global cookie preset at the given row index. */
  (e: 'selectPreset', index: number, value: string): void
}>()

const { translate } = useLocalization()

const showTooltip = computed(() => rows.length > 1)

/** Needed for type guard */
const handleUpserRow = (index: number, payload: TableRowUpsertPayload) => {
  const { value, ...rest } = payload

  // Type guard here as we cannot add files to params
  if (value instanceof File) {
    return
  }

  emit('upsert', index, { ...rest, value: value ?? '' })
}
</script>
<template>
  <CollapsibleSection
    class="group/params"
    :itemCount="rows.length">
    <template #title>{{ title }}</template>
    <template #actions>
      <div
        class="text-c-2 request-meta-buttons flex whitespace-nowrap opacity-0 group-hover/params:opacity-100 has-[:focus-visible]:opacity-100">
        <ScalarTooltip
          v-if="showTooltip"
          :content="
            translate('apiClient.requestParams.clearOptionalParameters')
          "
          placement="left">
          <ScalarButton
            :aria-label="
              translate('apiClient.requestParams.clearAll', { title })
            "
            class="pr-0.75 pl-1 transition-none"
            size="sm"
            variant="ghost"
            @click.stop="emit('deleteAll')">
            {{ translate('apiClient.requestParams.clear') }}
          </ScalarButton>
        </ScalarTooltip>
      </div>
    </template>
    <p
      v-if="description"
      class="text-c-2 px-3 py-2 text-sm">
      {{ description }}
    </p>
    <RequestTable
      class="flex-1"
      :columns="['32px', '', '']"
      :data="rows"
      :environment="environment"
      :exampleKey="exampleKey"
      :globalRoute="globalRoute"
      :invalidParams="invalidParams"
      :label="label"
      :showAddRowPlaceholder="showAddRowPlaceholder"
      @deleteRow="(index) => emit('delete', { index })"
      @navigate="(route) => eventBus.emit('ui:navigate', route)"
      @selectPreset="(index, value) => emit('selectPreset', index, value)"
      @upsertRow="handleUpserRow" />
  </CollapsibleSection>
</template>
